import { useState } from "react";
import { Sparkles, X, ArrowRight, Dumbbell, Target, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "fs_aanbeveling_verborgen_v1";

// Aanbevelingsmatrix op basis van doel + niveau
function bepaalAanbeveling(doel, niveau, user) {
  const heeftVrijeTraining = user?.vrije_training_enabled === true;
  const heeftSchemaMaker = user?.schema_builder_unlocked === true;
  const heeftPersoonlijk = Array.isArray(user?.purchased_schemas) && user.purchased_schemas.length > 0;
  const heeftCursus = user?.plan === "full";

  // Doel-specifieke aanbevelingen
  if (doel === "afvallen" && !heeftPersoonlijk) {
    return {
      key: "persoonlijk_schema",
      naam: "Persoonlijk Trainingsschema",
      prijs: "vanaf €9,95",
      icon: <Target size={18} />,
      kop: "Perfect voor jouw doel",
      reden: "Afvallen doe je het meest effectief met een schema dat precies is afgestemd op jouw caloriebehoefte en trainingsfrequentie.",
    };
  }
  if (doel === "sterker_worden" && !heeftPersoonlijk) {
    return {
      key: "persoonlijk_schema",
      naam: "Persoonlijk Trainingsschema",
      prijs: "vanaf €9,95",
      icon: <Target size={18} />,
      kop: "Sterker worden vereist structuur",
      reden: "Progressieve overbelasting werkt het best met een coach die jouw schema wekelijks aanpast op jouw progressie.",
    };
  }
  if (doel === "spieropbouw") {
    if ((niveau === "beginner" || !niveau) && !heeftPersoonlijk) {
      return {
        key: "persoonlijk_schema",
        naam: "Persoonlijk Trainingsschema",
        prijs: "vanaf €9,95",
        icon: <Target size={18} />,
        kop: "De snelste weg naar spiergroei",
        reden: "Als beginner bouw je het snelst massa op met een schema op maat — geen giswerk, pure progressie.",
      };
    }
    if ((niveau === "gevorderd" || niveau === "advanced") && !heeftSchemaMaker) {
      return {
        key: "schema_builder_unlock",
        naam: "Eigen Schemamaker",
        prijs: "€100 eenmalig",
        icon: <Sparkles size={18} />,
        kop: "Jij weet wat werkt",
        reden: "Op jouw niveau is de vrijheid om je eigen schema's te bouwen en aan te passen je grootste kracht.",
      };
    }
  }
  if (doel === "conditie" && !heeftVrijeTraining) {
    return {
      key: "vrije_training",
      naam: "Vrije Training",
      prijs: "€25 eenmalig",
      icon: <Dumbbell size={18} />,
      kop: "Flexibel trainen voor conditie",
      reden: "Conditie verbeter je met variatie — elke dag een andere training samenstellen geeft de beste resultaten.",
    };
  }
  if (doel === "onderhoud" && !heeftVrijeTraining) {
    return {
      key: "vrije_training",
      naam: "Vrije Training",
      prijs: "€25 eenmalig",
      icon: <Dumbbell size={18} />,
      kop: "Fit blijven op jouw eigen tempo",
      reden: "Voor onderhoud heb je flexibiliteit nodig — stel zelf trainingen samen wanneer en hoe het jou uitkomt.",
    };
  }

  // Fallback: meest logische beschikbare dienst
  if (!heeftPersoonlijk && !heeftCursus) {
    return {
      key: "persoonlijk_schema",
      naam: "Persoonlijk Trainingsschema",
      prijs: "vanaf €9,95",
      icon: <Target size={18} />,
      kop: "Stap naar jouw doel",
      reden: "Een schema op maat van je coach is de meest directe weg naar het behalen van jouw doelstelling.",
    };
  }

  return null; // Alles al in bezit of geen suggestie mogelijk
}

export default function SlimAanbevelingCard({ user }) {
  const [verborgen, setVerborgen] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "1"
  );
  const [loadingBetalen, setLoadingBetalen] = useState(false);

  const profiel = user?.profile || {};
  const aanbeveling = bepaalAanbeveling(profiel.doel, profiel.niveau, user);

  // Niet tonen als: geen profiel doel, alles al in bezit, of weggeklikt
  if (!profiel.doel || !aanbeveling || verborgen) return null;

  const handleVerbergen = () => {
    setVerborgen(true);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  const handleKopen = async () => {
    setLoadingBetalen(true);
    try {
      const r = await fetch(`${API}/api/payments/bundle-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ items: [{ product: aanbeveling.key }] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      window.location.href = d.checkout_url;
    } catch {
      // fallback: stuur naar bestelPagina
      window.location.href = `/bestellen?product=${aanbeveling.key}`;
    } finally {
      setLoadingBetalen(false);
    }
  };

  return (
    <div
      data-testid="slim-aanbeveling-card"
      className="relative rounded-2xl overflow-hidden mb-4"
      style={{ background: "linear-gradient(135deg, #0f0e0a 0%, #1a160a 100%)", border: "1px solid rgba(201,168,76,0.25)" }}
    >
      {/* Gouden accent lijn links */}
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: "#C9A84C" }} />

      <div className="pl-5 pr-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Icon */}
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C" }}
            >
              {aanbeveling.icon}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#C9A84C" }}>
                  Aanbevolen voor jou
                </p>
              </div>
              <p className="text-white font-bold text-sm leading-tight mb-1">{aanbeveling.kop}</p>
              <p className="text-zinc-500 text-xs leading-relaxed">{aanbeveling.reden}</p>
            </div>
          </div>

          {/* Sluit knop */}
          <button
            onClick={handleVerbergen}
            data-testid="aanbeveling-verbergen-btn"
            className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-zinc-600 hover:text-zinc-400 hover:bg-white/10 transition-all shrink-0 mt-0.5"
          >
            <X size={12} />
          </button>
        </div>

        {/* Dienst + CTA */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
          <div>
            <p className="text-zinc-300 text-sm font-semibold">{aanbeveling.naam}</p>
            <p className="text-zinc-600 text-xs">{aanbeveling.prijs}</p>
          </div>
          <button
            onClick={handleKopen}
            disabled={loadingBetalen}
            data-testid="aanbeveling-kopen-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-black transition-all active:scale-95 disabled:opacity-60"
            style={{ background: "#C9A84C" }}
          >
            {loadingBetalen ? (
              <div className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <><ArrowRight size={12} /> Bekijk & koop</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
